import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for tracking min/max bounds of parameters across the browser session
 * @param {Object} timeSeriesData - Time series data for all parameters {tag: [{timestamp, value}]}
 * @returns {Object} { parameterBounds } - Object with min/max for each parameter tag
 */
function useParameterBounds(timeSeriesData) {
  const [parameterBounds, setParameterBounds] = useState({});
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!timeSeriesData || Object.keys(timeSeriesData).length === 0) {
      return;
    }

    setParameterBounds((prevBounds) => {
      const newBounds = { ...prevBounds };
      let hasUpdates = false;

      Object.entries(timeSeriesData).forEach(([tag, dataPoints]) => {
        if (!Array.isArray(dataPoints) || dataPoints.length === 0) {
          return;
        }

        const values = dataPoints.map((d) => d.value);
        const currentMin = Math.min(...values);
        const currentMax = Math.max(...values);

        if (!newBounds[tag]) {
          // First time seeing this tag
          newBounds[tag] = {
            min: currentMin,
            max: currentMax,
          };
          hasUpdates = true;
        } else {
          // Update bounds if we have new extremes
          const updatedMin = Math.min(newBounds[tag].min, currentMin);
          const updatedMax = Math.max(newBounds[tag].max, currentMax);

          if (
            updatedMin !== newBounds[tag].min ||
            updatedMax !== newBounds[tag].max
          ) {
            newBounds[tag] = {
              min: updatedMin,
              max: updatedMax,
            };
            hasUpdates = true;
          }
        }
      });

      // Only update state if there were actual changes
      return hasUpdates ? newBounds : prevBounds;
    });

    if (!isInitialized.current) {
      isInitialized.current = true;
    }
  }, [timeSeriesData]);

  return { parameterBounds };
}

export default useParameterBounds;
